package er.prateek.crossly.ads.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.ads.AdsConfig
import er.prateek.crossly.ads.AdsPolicy
import er.prateek.crossly.di.AppContainer

/** The IAB standard banner height, and what every platform host here requests. */
val BannerHeight: Dp = 50.dp

/**
 * Drop this at the bottom of a screen. It renders nothing at all -- no reserved space, no
 * placeholder -- unless this placement is switched on for this user, so a paid-tier or
 * ads-off build looks like ads were never designed in.
 */
@Composable
fun AdBannerSlot(
    placement: AdPlacement,
    modifier: Modifier = Modifier,
) {
    val ads = AppContainer.adsCoordinator
    val config by ads.config.collectAsStateWithLifecycle()
    val entitlement by ads.entitlement.collectAsStateWithLifecycle()

    if (!AdsPolicy.bannerAllowed(config, entitlement, placement)) return

    LaunchedEffect(placement) { ads.logBannerShown(placement) }


    Box(
        modifier = modifier.fillMaxWidth().height(BannerHeight),
        contentAlignment = Alignment.Center,
    ) {
        PlatformAdBanner(config = config, placement = placement)
    }
}

/**
 * How much bottom space a screen must keep clear for [placement]'s banner -- zero whenever
 * no banner will be drawn.
 *
 * Reserving the space in the layout, rather than letting the banner float over the content,
 * is what stops an ad from covering the last row of a grid or a primary button. `GameScreen`
 * applies this for every screen, so no individual screen has to think about it.
 */
@Composable
fun rememberBannerReservedHeight(placement: AdPlacement?): Dp {
    if (placement == null) return 0.dp
    val ads = AppContainer.adsCoordinator
    val config by ads.config.collectAsStateWithLifecycle()
    val entitlement by ads.entitlement.collectAsStateWithLifecycle()
    return if (AdsPolicy.bannerAllowed(config, entitlement, placement)) BannerHeight else 0.dp
}

/**
 * The actual ad view. Android/iOS embed a native banner view via Compose interop; web
 * positions an AdSense `<ins>` element over the canvas; desktop draws nothing.
 */
@Composable
expect fun PlatformAdBanner(
    config: AdsConfig,
    placement: AdPlacement,
    modifier: Modifier = Modifier,
)
